/**
 * SE-1765: hook that fetches the caller's per-network feature flags
 * from `GET /api/network-features` and folds them into the
 * `networkFeatures` Redux slice.
 *
 * Fetches ONCE per authenticated session (idempotent). Consumers read
 * the slice directly via `selectAggregateFeatures` /
 * `selectFeaturesForProgram`.
 */
import { useEffect, useRef } from "react";
import { API } from "aws-amplify";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  NetworkFeaturesResponse,
  clearNetworkFeatures,
  mergeProgramFeatures,
  selectFeaturesForProgram,
  selectNetworkFeaturesState,
  setNetworkFeatures,
  setNetworkFeaturesError,
  setNetworkFeaturesStatus,
} from "./networkFeaturesSlice";

const fetchNetworkFeatures = async (): Promise<NetworkFeaturesResponse> =>
  (await API.get(
    "simpl-api",
    "network-features",
    {},
  )) as NetworkFeaturesResponse;

const fetchNetworkFeaturesForProgram = async (
  programId: string,
): Promise<NetworkFeaturesResponse> =>
  (await API.get("simpl-api", "network-features", {
    queryStringParameters: { programId },
  })) as NetworkFeaturesResponse;

/**
 * Ensures the network-features slice is populated for the current user.
 *
 * `hasUser` is what the caller (usually `Layout.tsx`) knows — the
 * current user has been resolved by the auth flow. We ignore the fetch
 * until then; a signed-out state clears the slice so a next sign-in
 * starts clean.
 */
export const useLoadNetworkFeatures = (hasUser: boolean) => {
  const dispatch = useAppDispatch();
  const { status } = useAppSelector(selectNetworkFeaturesState);
  // Ref-mirror so the effect can consult the current status without
  // listing it in deps. Listing status in deps causes the effect to
  // re-run when the hook dispatches setNetworkFeaturesStatus("loading"),
  // and the re-run's cleanup cancels the still-in-flight .then chain —
  // the fetch returns 200 but never lands in the store.
  const statusRef = useRef(status);
  statusRef.current = status;

  useEffect(() => {
    if (!hasUser) {
      // Sign-out — clear any cached flags so a subsequent sign-in as a
      // different user doesn't briefly see the prior user's tabs.
      if (statusRef.current !== "idle") dispatch(clearNetworkFeatures());
      return;
    }
    if (statusRef.current === "loading" || statusRef.current === "succeeded") {
      return;
    }

    let cancelled = false;
    let settled = false;
    dispatch(setNetworkFeaturesStatus("loading"));
    dispatch(setNetworkFeaturesError(undefined));

    fetchNetworkFeatures()
      .then((data) => {
        settled = true;
        if (cancelled) return;
        dispatch(setNetworkFeatures(data));
        dispatch(setNetworkFeaturesStatus("succeeded"));
      })
      .catch((err: unknown) => {
        settled = true;
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        // eslint-disable-next-line no-console
        console.error("network-features fetch failed", err);
        dispatch(setNetworkFeaturesError(message));
        dispatch(setNetworkFeaturesStatus("failed"));
      });

    return () => {
      cancelled = true;
      // Unmounted mid-fetch (e.g. the EULA gate swapping the tree on
      // first sign-in): drop back to idle so the next mount refetches
      // instead of seeing "loading" forever.
      if (!settled) dispatch(setNetworkFeaturesStatus("idle"));
    };
    // Intentionally omits status: see the statusRef comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUser, dispatch]);
};

/**
 * SE-1765: on-demand fetch for a program the caller isn't a member of.
 *
 * The initial `useLoadNetworkFeatures` fetch returns only programs the
 * caller has an active membership in. A SysAdmin picking any program in
 * the system, or a program admin looking up flags for a program they're
 * inviting into but don't otherwise participate in, needs the target
 * program's flags too. This hook fetches them for a specific programId
 * and merges into the same `byProgramId` map, so downstream selectors
 * (`selectFeaturesForProgram`) just work.
 *
 * Fetch is skipped when `programId` is falsy or the map already has an
 * entry — this is `byProgramId` cache-first, ONE round-trip per program
 * the picker lands on. The Lambda's per-program branch is a plain
 * network→feature lookup: not sensitive info, no authority check
 * (mirrors what any authenticated user can already infer from the SPA's
 * behavior).
 */
export const useLoadFeaturesForProgram = (
  programId: string | null | undefined,
) => {
  const dispatch = useAppDispatch();
  const cached = useAppSelector(selectFeaturesForProgram(programId));
  const { data } = useAppSelector(selectNetworkFeaturesState);
  const known = !!(programId && data?.byProgramId[programId]);

  useEffect(() => {
    if (!programId) return;
    if (known) return;

    let cancelled = false;
    fetchNetworkFeaturesForProgram(programId)
      .then((resp) => {
        if (cancelled) return;
        const flags = resp.byProgramId[programId] ?? resp.aggregate;
        dispatch(mergeProgramFeatures({ programId, flags }));
      })
      .catch((err: unknown) => {
        // Non-fatal: the invitation form treats "no flags known" as
        // "role hidden," which is the safe default. Log for triage.
        // eslint-disable-next-line no-console
        console.error(
          `network-features fetch for program ${programId} failed`,
          err,
        );
      });

    return () => {
      cancelled = true;
    };
  }, [programId, known, dispatch]);

  return cached;
};
